// services/omoreInteractionService.ts
// Firestore queries for Ask Omore interaction tracking and moderation

import {
    collection,
    query,
    where,
    getDocs,
    doc,
    getDoc,
    setDoc,
    orderBy,
    limit,
    Timestamp,
    onSnapshot,
    QueryConstraint
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
    OmoreSession,
    OmoreMessage,
    FlaggedMessage,
    UserAbuseSummary,
    OmoreInteractionDailyAnalytics,
    OmoreInteractionAggregatedAnalytics,
    OmoreSessionDetail,
    ModerationQueueFilter,
    PaginatedFlaggedResponse,
    OmoreAnalyticsFilter,
    ModerationAction
} from '../types/OmoreInteractionTypes';

// ─── Collection Paths ────────────────────────────────────────

const SESSIONS_PATH = 'omoreInteractions/sessions/items';
const MESSAGES_PATH = 'omoreInteractions/messages/items';
const ANALYTICS_DAILY_PATH = 'omoreInteractions/analytics/daily';
const MODERATION_FLAGGED_PATH = 'omoreInteractions/moderation/flagged';
const MODERATION_USERS_PATH = 'omoreInteractions/moderation/users';

// ─── Helper: convert Firestore doc to typed session ─────────

function docToSession(data: Record<string, any>, id: string): OmoreSession {
    return {
        id,
        appleID: data.appleID || '',
        profileID: data.profileID || '',
        explorerName: data.explorerName || '',
        helperType: data.helperType || 'general',
        origin: data.origin || 'home',
        startedAt: data.startedAt,
        endedAt: data.endedAt || undefined,
        durationSeconds: data.durationSeconds || 0,
        messageCount: data.messageCount || 0,
        totalTokensUsed: data.totalTokensUsed || 0,
        suggestionsShown: data.suggestionsShown || 0,
        suggestionsAdopted: data.suggestionsAdopted || 0,
        hasModerationFlag: data.hasModerationFlag || false,
        deviceType: data.deviceType || 'iPhone',
        appVersion: data.appVersion || '',
    };
}

function docToMessage(data: Record<string, any>, id: string): OmoreMessage {
    return {
        id,
        sessionID: data.sessionID || '',
        role: data.role || 'user',
        content: data.content || '',
        timestamp: data.timestamp,
        tokenCount: data.tokenCount || 0,
        isSuggestion: data.isSuggestion || false,
        suggestionAdopted: data.suggestionAdopted,
        moderationFlags: data.moderationFlags || undefined,
        moderationSeverity: data.moderationSeverity || undefined,
    };
}

function docToFlaggedMessage(data: Record<string, any>, id: string): FlaggedMessage {
    return {
        id,
        sessionID: data.sessionID || '',
        appleID: data.appleID || '',
        profileID: data.profileID || '',
        explorerName: data.explorerName || '',
        helperType: data.helperType || 'general',
        origin: data.origin || 'home',
        messageContent: data.messageContent || '',
        flagCategories: data.flagCategories || [],
        maxSeverity: data.maxSeverity || 1,
        flaggedAt: data.flaggedAt,
        reviewedAt: data.reviewedAt || undefined,
        reviewedBy: data.reviewedBy || undefined,
        reviewAction: data.reviewAction || undefined,
        reviewNotes: data.reviewNotes || undefined,
    };
}

function docToUserAbuseSummary(data: Record<string, any>, id: string): UserAbuseSummary {
    return {
        appleID: id,
        profileID: data.profileID || '',
        explorerName: data.explorerName || '',
        totalFlags: data.totalFlags || 0,
        flagsByCategory: data.flagsByCategory || {},
        highestSeverity: data.highestSeverity || 1,
        firstFlaggedAt: data.firstFlaggedAt,
        lastFlaggedAt: data.lastFlaggedAt,
        isBlocked: data.isBlocked || false,
        blockedAt: data.blockedAt || undefined,
        blockedReason: data.blockedReason || undefined,
        blockedBy: data.blockedBy || undefined,
        sampleMessageIDs: data.sampleMessageIDs || [],
    };
}

// ─── Sessions ────────────────────────────────────────────────

/**
 * Fetch recent sessions, optionally filtered by date range
 */
export async function fetchRecentSessions(limit_count: number = 50): Promise<OmoreSession[]> {
    try {
        const q = query(
            collection(db, SESSIONS_PATH),
            orderBy('startedAt', 'desc'),
            limit(limit_count)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => docToSession(d.data(), d.id));
    } catch (error) {
        console.error('Error fetching recent sessions:', error);
        return [];
    }
}

/**
 * Fetch a single session by ID
 */
export async function fetchSession(sessionID: string): Promise<OmoreSession | null> {
    try {
        const docSnap = await getDoc(doc(db, SESSIONS_PATH, sessionID));
        if (!docSnap.exists()) return null;
        return docToSession(docSnap.data(), docSnap.id);
    } catch (error) {
        console.error('Error fetching session:', error);
        return null;
    }
}

/**
 * Fetch full session detail: session metadata + ordered messages
 */
export async function fetchSessionDetails(sessionID: string): Promise<OmoreSessionDetail | null> {
    try {
        const [sessionSnap, messagesSnap] = await Promise.all([
            getDoc(doc(db, SESSIONS_PATH, sessionID)),
            getDocs(
                query(
                    collection(db, MESSAGES_PATH),
                    where('sessionID', '==', sessionID),
                    orderBy('timestamp', 'asc')
                )
            )
        ]);

        if (!sessionSnap.exists()) return null;

        const session = docToSession(sessionSnap.data(), sessionSnap.id);
        const messages = messagesSnap.docs.map(d => docToMessage(d.data(), d.id));

        return {
            session,
            messages,
            explorerName: session.explorerName,
        };
    } catch (error) {
        console.error('Error fetching session details:', error);
        return null;
    }
}

// ─── Moderation Queue ────────────────────────────────────────

/**
 * Fetch the moderation queue with pagination and filtering
 */
export async function fetchModerationQueue(
    filter: ModerationQueueFilter = {}
): Promise<PaginatedFlaggedResponse> {
    try {
        const constraints: QueryConstraint[] = [];

        // Review status filter
        const reviewStatus = filter.reviewStatus || 'unreviewed';
        if (reviewStatus === 'unreviewed') {
            constraints.push(where('reviewAction', '==', null));
        } else if (reviewStatus === 'reviewed') {
            constraints.push(where('reviewAction', '!=', null));
        }

        // Category filter – Firestore array-contains-any
        if (filter.categories && filter.categories.length > 0) {
            constraints.push(where('flagCategories', 'array-contains-any', filter.categories));
        }

        // Date range
        if (filter.startDate) {
            constraints.push(where('flaggedAt', '>=', Timestamp.fromDate(new Date(filter.startDate))));
        }
        if (filter.endDate) {
            constraints.push(where('flaggedAt', '<=', Timestamp.fromDate(new Date(filter.endDate + 'T23:59:59'))));
        }

        // Sort
        const sortField = filter.sortBy || 'flaggedAt';
        const sortDir = filter.sortDirection || 'desc';
        constraints.push(orderBy(sortField, sortDir));

        // Fetch all matching (pagination done client-side to avoid cursor complexity)
        const snapshot = await getDocs(query(collection(db, MODERATION_FLAGGED_PATH), ...constraints));
        const allItems = snapshot.docs.map(d => docToFlaggedMessage(d.data(), d.id));

        // Client-side pagination
        const pageSize = filter.pageSize || 25;
        const page = filter.page || 0;
        const totalCount = allItems.length;
        const totalPages = Math.ceil(totalCount / pageSize) || 1;
        const startIndex = page * pageSize;
        const items = allItems.slice(startIndex, startIndex + pageSize);

        return { items, totalCount, totalPages, currentPage: page };
    } catch (error) {
        console.error('Error fetching moderation queue:', error);
        return { items: [], totalCount: 0, totalPages: 1, currentPage: 0 };
    }
}

/**
 * Real-time subscription to unreviewed flagged messages.
 * Returns an unsubscribe function.
 */
export function subscribeToFlaggedMessages(
    callback: (messages: FlaggedMessage[]) => void
): () => void {
    const q = query(
        collection(db, MODERATION_FLAGGED_PATH),
        where('reviewAction', '==', null),
        orderBy('flaggedAt', 'desc')
    );

    return onSnapshot(q, (snapshot) => {
        const messages = snapshot.docs.map(d => docToFlaggedMessage(d.data(), d.id));
        callback(messages);
    });
}

/**
 * Submit a review action on a flagged message
 */
export async function reviewFlaggedMessage(
    messageID: string,
    action: ModerationAction,
    reviewerEmail: string,
    notes?: string
): Promise<void> {
    try {
        await setDoc(
            doc(db, MODERATION_FLAGGED_PATH, messageID),
            {
                reviewAction: action,
                reviewedAt: Timestamp.now(),
                reviewedBy: reviewerEmail,
                reviewNotes: notes || '',
            },
            { merge: true }
        );
    } catch (error) {
        console.error('Error reviewing flagged message:', error);
        throw error;
    }
}

// ─── User Abuse ──────────────────────────────────────────────

/**
 * Fetch all users with at least one flag, sorted by total flags descending
 */
export async function fetchFlaggedUsers(limit_count: number = 100): Promise<UserAbuseSummary[]> {
    try {
        const q = query(
            collection(db, MODERATION_USERS_PATH),
            orderBy('totalFlags', 'desc'),
            limit(limit_count)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => docToUserAbuseSummary(d.data(), d.id));
    } catch (error) {
        console.error('Error fetching flagged users:', error);
        return [];
    }
}

/**
 * Fetch a single user's abuse summary
 */
export async function fetchUserAbuseSummary(appleID: string): Promise<UserAbuseSummary | null> {
    try {
        const docSnap = await getDoc(doc(db, MODERATION_USERS_PATH, appleID));
        if (!docSnap.exists()) return null;
        return docToUserAbuseSummary(docSnap.data(), docSnap.id);
    } catch (error) {
        console.error('Error fetching user abuse summary:', error);
        return null;
    }
}

/**
 * Fetch sample flagged messages for a user (up to 10)
 */
export async function fetchUserFlaggedMessages(appleID: string): Promise<FlaggedMessage[]> {
    try {
        const q = query(
            collection(db, MODERATION_FLAGGED_PATH),
            where('appleID', '==', appleID),
            orderBy('flaggedAt', 'desc'),
            limit(10)
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => docToFlaggedMessage(d.data(), d.id));
    } catch (error) {
        console.error('Error fetching user flagged messages:', error);
        return [];
    }
}

/**
 * Block or unblock a user
 */
export async function setUserBlockStatus(
    appleID: string,
    isBlocked: boolean,
    adminEmail: string,
    reason?: string
): Promise<void> {
    try {
        const update: Record<string, any> = {
            isBlocked,
        };
        if (isBlocked) {
            update.blockedAt = Timestamp.now();
            update.blockedBy = adminEmail;
            update.blockedReason = reason || '';
        } else {
            update.blockedAt = null;
            update.blockedBy = null;
            update.blockedReason = null;
        }
        await setDoc(doc(db, MODERATION_USERS_PATH, appleID), update, { merge: true });
    } catch (error) {
        console.error('Error updating user block status:', error);
        throw error;
    }
}

// ─── Analytics ───────────────────────────────────────────────

/**
 * Fetch Omore daily analytics for a date range
 */
export async function fetchOmoreAnalytics(
    filter: OmoreAnalyticsFilter
): Promise<OmoreInteractionAggregatedAnalytics> {
    try {
        const q = query(
            collection(db, ANALYTICS_DAILY_PATH),
            where('date', '>=', filter.startDate),
            where('date', '<=', filter.endDate),
            orderBy('date', 'asc')
        );
        const snapshot = await getDocs(q);

        const dailyStats: OmoreInteractionDailyAnalytics[] = snapshot.docs.map(d => {
            const data = d.data();
            return {
                date: data.date || d.id,
                totalSessions: data.totalSessions || 0,
                totalMessages: data.totalMessages || 0,
                uniqueUsers: data.uniqueUsers || 0,
                sessionsByHelperType: data.sessionsByHelperType || {},
                sessionsByOrigin: data.sessionsByOrigin || {},
                totalSuggestionsShown: data.totalSuggestionsShown || 0,
                totalSuggestionsAdopted: data.totalSuggestionsAdopted || 0,
                avgSessionDurationSeconds: data.avgSessionDurationSeconds || 0,
                totalTokensUsed: data.totalTokensUsed || 0,
                flaggedMessageCount: data.flaggedMessageCount || 0,
            };
        });

        // Aggregate across all days
        const totals = dailyStats.reduce(
            (acc, day) => ({
                totalSessions: acc.totalSessions + day.totalSessions,
                totalMessages: acc.totalMessages + day.totalMessages,
                totalSuggestionsShown: acc.totalSuggestionsShown + day.totalSuggestionsShown,
                totalSuggestionsAdopted: acc.totalSuggestionsAdopted + day.totalSuggestionsAdopted,
                totalDuration: acc.totalDuration + (day.avgSessionDurationSeconds * day.totalSessions),
                sessionsByHelperType: mergeRecords(acc.sessionsByHelperType, day.sessionsByHelperType),
                sessionsByOrigin: mergeRecords(acc.sessionsByOrigin, day.sessionsByOrigin),
            }),
            {
                totalSessions: 0,
                totalMessages: 0,
                totalSuggestionsShown: 0,
                totalSuggestionsAdopted: 0,
                totalDuration: 0,
                sessionsByHelperType: {} as Record<string, number>,
                sessionsByOrigin: {} as Record<string, number>,
            }
        );

        // Unique users: union across days (approximate – sum unique per day)
        const uniqueUsers = dailyStats.reduce((sum, day) => sum + day.uniqueUsers, 0);

        const suggestionAdoptionRate = totals.totalSuggestionsShown > 0
            ? (totals.totalSuggestionsAdopted / totals.totalSuggestionsShown) * 100
            : 0;

        const avgSessionDurationSeconds = totals.totalSessions > 0
            ? totals.totalDuration / totals.totalSessions
            : 0;

        return {
            startDate: filter.startDate,
            endDate: filter.endDate,
            totalSessions: totals.totalSessions,
            totalMessages: totals.totalMessages,
            uniqueUsers,
            suggestionAdoptionRate,
            avgSessionDurationSeconds,
            sessionsByHelperType: totals.sessionsByHelperType,
            sessionsByOrigin: totals.sessionsByOrigin,
            dailyStats,
        };
    } catch (error) {
        console.error('Error fetching Omore analytics:', error);
        return {
            startDate: filter.startDate,
            endDate: filter.endDate,
            totalSessions: 0,
            totalMessages: 0,
            uniqueUsers: 0,
            suggestionAdoptionRate: 0,
            avgSessionDurationSeconds: 0,
            sessionsByHelperType: {},
            sessionsByOrigin: {},
            dailyStats: [],
        };
    }
}

// ─── Week-over-Week / Month-over-Month Trends ──────────────

export interface TrendComparison {
    metric: string;
    currentValue: number;
    previousValue: number;
    change: number;
    changePercent: number;
    trend: 'up' | 'down' | 'stable';
}

export interface WeekOverWeekComparison {
    currentWeek: {
        startDate: string;
        endDate: string;
        analytics: OmoreInteractionAggregatedAnalytics;
    };
    previousWeek: {
        startDate: string;
        endDate: string;
        analytics: OmoreInteractionAggregatedAnalytics;
    };
    trends: {
        sessions: TrendComparison;
        uniqueUsers: TrendComparison;
        messages: TrendComparison;
        avgSessionDuration: TrendComparison;
        suggestionAdoptionRate: TrendComparison;
        flagRate: TrendComparison;
    };
    helperTypeTrends: Array<{
        helperType: string;
        currentCount: number;
        previousCount: number;
        change: number;
        changePercent: number;
    }>;
    topGrowthAreas: string[];
    topDeclineAreas: string[];
}

function calculateTrend(metric: string, currentValue: number, previousValue: number): TrendComparison {
    const change = currentValue - previousValue;
    const changePercent = previousValue > 0
        ? (change / previousValue) * 100
        : currentValue > 0 ? 100 : 0;

    let trend: 'up' | 'down' | 'stable';
    if (Math.abs(changePercent) < 5) {
        trend = 'stable';
    } else if (changePercent > 0) {
        trend = 'up';
    } else {
        trend = 'down';
    }

    return { metric, currentValue, previousValue, change, changePercent, trend };
}

function formatDateStr(date: Date): string {
    return date.toISOString().split('T')[0];
}

/**
 * Fetch week-over-week comparison (last 7 days vs previous 7 days)
 */
export async function fetchWeekOverWeekComparison(): Promise<WeekOverWeekComparison> {
    const now = new Date();
    const currentEnd = new Date(now);
    const currentStart = new Date(now);
    currentStart.setDate(currentStart.getDate() - 6);

    const previousEnd = new Date(currentStart);
    previousEnd.setDate(previousEnd.getDate() - 1);
    const previousStart = new Date(previousEnd);
    previousStart.setDate(previousStart.getDate() - 6);

    return buildComparison(currentStart, currentEnd, previousStart, previousEnd);
}

/**
 * Fetch month-over-month comparison (last 30 days vs previous 30 days)
 */
export async function fetchMonthOverMonthComparison(): Promise<WeekOverWeekComparison> {
    const now = new Date();
    const currentEnd = new Date(now);
    const currentStart = new Date(now);
    currentStart.setDate(currentStart.getDate() - 29);

    const previousEnd = new Date(currentStart);
    previousEnd.setDate(previousEnd.getDate() - 1);
    const previousStart = new Date(previousEnd);
    previousStart.setDate(previousStart.getDate() - 29);

    return buildComparison(currentStart, currentEnd, previousStart, previousEnd);
}

async function buildComparison(
    currentStart: Date,
    currentEnd: Date,
    previousStart: Date,
    previousEnd: Date
): Promise<WeekOverWeekComparison> {
    const [currentAnalytics, previousAnalytics] = await Promise.all([
        fetchOmoreAnalytics({
            startDate: formatDateStr(currentStart),
            endDate: formatDateStr(currentEnd),
        }),
        fetchOmoreAnalytics({
            startDate: formatDateStr(previousStart),
            endDate: formatDateStr(previousEnd),
        }),
    ]);

    // Calculate flag rates from daily stats
    const currentFlaggedCount = currentAnalytics.dailyStats.reduce((s, d) => s + d.flaggedMessageCount, 0);
    const previousFlaggedCount = previousAnalytics.dailyStats.reduce((s, d) => s + d.flaggedMessageCount, 0);
    const currentFlagRate = currentAnalytics.totalMessages > 0
        ? (currentFlaggedCount / currentAnalytics.totalMessages) * 100
        : 0;
    const previousFlagRate = previousAnalytics.totalMessages > 0
        ? (previousFlaggedCount / previousAnalytics.totalMessages) * 100
        : 0;

    const trends = {
        sessions: calculateTrend('Sessions', currentAnalytics.totalSessions, previousAnalytics.totalSessions),
        uniqueUsers: calculateTrend('Unique Users', currentAnalytics.uniqueUsers, previousAnalytics.uniqueUsers),
        messages: calculateTrend('Messages', currentAnalytics.totalMessages, previousAnalytics.totalMessages),
        avgSessionDuration: calculateTrend('Avg Session Duration', currentAnalytics.avgSessionDurationSeconds, previousAnalytics.avgSessionDurationSeconds),
        suggestionAdoptionRate: calculateTrend('Suggestion Adoption', currentAnalytics.suggestionAdoptionRate, previousAnalytics.suggestionAdoptionRate),
        flagRate: calculateTrend('Flag Rate', currentFlagRate, previousFlagRate),
    };

    // Helper type trends
    const allHelperTypes = new Set([
        ...Object.keys(currentAnalytics.sessionsByHelperType),
        ...Object.keys(previousAnalytics.sessionsByHelperType),
    ]);

    const helperTypeTrends = Array.from(allHelperTypes).map(ht => {
        const currentCount = currentAnalytics.sessionsByHelperType[ht] || 0;
        const previousCount = previousAnalytics.sessionsByHelperType[ht] || 0;
        const change = currentCount - previousCount;
        const changePercent = previousCount > 0
            ? (change / previousCount) * 100
            : currentCount > 0 ? 100 : 0;
        return { helperType: ht, currentCount, previousCount, change, changePercent };
    }).sort((a, b) => b.changePercent - a.changePercent);

    // Top growth / decline areas
    const topGrowthAreas: string[] = [];
    const topDeclineAreas: string[] = [];

    Object.values(trends).forEach(t => {
        if (t.trend === 'up' && t.metric !== 'Flag Rate') topGrowthAreas.push(t.metric);
        if (t.trend === 'down' && t.metric !== 'Flag Rate') topDeclineAreas.push(t.metric);
        // Flag rate increase is a decline area
        if (t.metric === 'Flag Rate' && t.trend === 'up') topDeclineAreas.push('Increasing Flag Rate');
        if (t.metric === 'Flag Rate' && t.trend === 'down') topGrowthAreas.push('Decreasing Flag Rate');
    });

    helperTypeTrends.forEach(ht => {
        if (ht.changePercent > 20) topGrowthAreas.push(`${ht.helperType} helper`);
        if (ht.changePercent < -20) topDeclineAreas.push(`${ht.helperType} helper`);
    });

    return {
        currentWeek: {
            startDate: formatDateStr(currentStart),
            endDate: formatDateStr(currentEnd),
            analytics: currentAnalytics,
        },
        previousWeek: {
            startDate: formatDateStr(previousStart),
            endDate: formatDateStr(previousEnd),
            analytics: previousAnalytics,
        },
        trends,
        helperTypeTrends,
        topGrowthAreas,
        topDeclineAreas,
    };
}

// ─── Utility ─────────────────────────────────────────────────

/** Merge two Record<string, number> by summing values for matching keys */
function mergeRecords(a: Record<string, number>, b: Record<string, number>): Record<string, number> {
    const result = { ...a };
    for (const [key, val] of Object.entries(b)) {
        result[key] = (result[key] || 0) + val;
    }
    return result;
}
